import { useState, useRef, useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import apiClient from '@/api/client';
import { QUERY_KEYS } from '@/api/queryKeys';
import { useAuthStore } from '@/store/useAuthStore';
import { useConfirm } from '@/components/common/ConfirmDialog';

// ─── Typen ──────────────────────────────────────────────────────────────────

interface Media {
  id: string;
  filename: string;
  originalName: string | null;
  mimeType: string | null;
  title: string | null;
  description: string | null;
  dateTaken: string | null;
  createdAt: string;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

function usePersonMedia(personId: string) {
  return useQuery<Media[]>({
    queryKey: ['media', personId],
    queryFn: async () => {
      const { data } = await apiClient.get<Media[]>(`/persons/${personId}/media`);
      return data;
    },
  });
}

function useDeleteMedia(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: string) => {
      await apiClient.delete(`/media/${mediaId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media', personId] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(personId) });
      // War die Datei das Profilbild, zeigt auch der Baum sie nicht mehr
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      toast.success('Medien-Datei gelöscht.');
    },
    onError: () => {
      toast.error('Datei konnte nicht gelöscht werden.');
    },
  });
}

// ─── Lightbox ───────────────────────────────────────────────────────────────

function Lightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-8"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/40"
        aria-label="Schließen"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-full max-w-full rounded object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ─── MediaTab ───────────────────────────────────────────────────────────────

interface MediaTabProps {
  personId: string;
}

export function MediaTab({ personId }: MediaTabProps) {
  const canManage = useAuthStore((s) => s.hasPermission('persons.manage'));
  const accessToken = useAuthStore((s) => s.accessToken);
  const { data: mediaItems, isLoading } = usePersonMedia(personId);
  const deleteMedia = useDeleteMedia(personId);
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxSrc, setLightboxSrc] = useState<{ src: string; alt: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      setUploading(true);
      let uploaded = 0;
      const failed: string[] = [];

      // Jede Datei einzeln behandeln: Schlägt eine fehl, gehen die bereits
      // hochgeladenen nicht "verloren" und die Meldung bleibt ehrlich.
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          await apiClient.post(`/persons/${personId}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          uploaded++;
        } catch {
          failed.push(file.name);
        }
      }

      if (uploaded > 0) {
        queryClient.invalidateQueries({ queryKey: ['media', personId] });
        // Erstes Bild wird ggf. automatisch Profilbild → Detail + Baum aktualisieren
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(personId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tree.all });
      }
      if (failed.length === 0) {
        toast.success(`${uploaded} Datei(en) hochgeladen.`);
      } else if (uploaded > 0) {
        toast.error(`${uploaded} hochgeladen, fehlgeschlagen: ${failed.join(', ')}`);
      } else {
        toast.error('Upload fehlgeschlagen.');
      }

      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [personId, queryClient],
  );

  const handleDelete = useCallback(
    async (mediaId: string, name: string) => {
      const ok = await confirm({
        title: 'Datei löschen?',
        message: (
          <>
            &quot;<strong>{name}</strong>&quot; wirklich löschen?
          </>
        ),
        confirmLabel: 'Löschen',
        variant: 'danger',
      });
      if (!ok) return;
      deleteMedia.mutate(mediaId);
    },
    [confirm, deleteMedia],
  );

  return (
    <div className="space-y-4">
      {/* Upload-Button (nur für Admins) */}
      {canManage && (
        <div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300
                       py-4 text-sm font-medium text-slate-500 transition-colors
                       hover:border-blue-400 hover:text-blue-600 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
                Wird hochgeladen...
              </>
            ) : (
              <>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Fotos / Dokumente hochladen
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.heic"
            multiple
            className="hidden"
            onChange={handleUpload}
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
        </div>
      )}

      {/* Leerer Zustand */}
      {mediaItems && mediaItems.length === 0 && (
        <p className="py-4 text-center text-sm text-slate-400">
          Noch keine Medien vorhanden.
        </p>
      )}

      {/* Foto-Grid (3 Spalten) */}
      {mediaItems && mediaItems.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {mediaItems.map((media) => {
            const isImage = media.mimeType?.startsWith('image/');
            const src = accessToken ? `/uploads/${media.filename}` : undefined;
            const displayName = media.originalName ?? media.filename;

            return (
              <div key={media.id} className="group relative">
                {/* Thumbnail */}
                <button
                  onClick={() => {
                    if (!src) return;
                    if (isImage) setLightboxSrc({ src, alt: displayName });
                    else window.open(src, '_blank');
                  }}
                  className="aspect-square w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition-shadow hover:shadow-md"
                >
                  {isImage && src !== undefined ? (
                    <img
                      src={src}
                      alt={displayName}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center p-2">
                      <svg className="h-8 w-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                      <span className="mt-1 truncate text-[10px] text-slate-400">
                        {displayName}
                      </span>
                    </div>
                  )}
                </button>

                {/* Löschen-Button (Hover, nur Admin) */}
                {canManage && (
                  <button
                    onClick={() => handleDelete(media.id, displayName)}
                    className="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white opacity-0 shadow transition-opacity
                               group-hover:opacity-100 hover:bg-red-600"
                    aria-label={`${displayName} löschen`}
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightboxSrc && (
        <Lightbox
          src={lightboxSrc.src}
          alt={lightboxSrc.alt}
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </div>
  );
}
